export interface ThemeDefinition {
  id: string;
  name: string;
  description?: string;
  colorScheme: 'light' | 'dark';
  preview: string[];
  cssVars: Record<`--${string}`, string>;
}

// Paleta 8 nasyconych kolorów przypisywanych graczom (kolejno i bez duplikatów)
export const PLAYER_COLORS = [
  '#FF4D4F', // czerwony
  '#FF8A00', // pomarańcz
  '#F7B500', // żółty
  '#2ECC71', // zielony
  '#00B5E2', // cyjan
  '#8B5E3C', // brązowy (zamiast drugiego niebieskiego)
  '#9B51E0', // fiolet
  '#FF3DBE', // magenta
] as const;

export type ThemeId = ThemeDefinition['id'];

const METRIC_COLOR_VARS_BY_SCHEME: Record<ThemeDefinition['colorScheme'], Record<`--${string}`, string>> = {
  dark: {
    '--color-results-metric-positive': '#4ADE80',
    '--color-results-metric-negative': '#FB7185',
    '--color-results-metric-caution': '#FCD34D',
  },
  light: {
    '--color-results-metric-positive': '#149467',
    '--color-results-metric-negative': '#D94F70',
    '--color-results-metric-caution': '#8A5200',
  },
};

const baseThemesInternal: ThemeDefinition[] = [
  {
    id: 'deep-sea',
    name: 'Głęboki ocean',
    description: 'Oceaniczne błękity z bursztynowym akcentem',
    colorScheme: 'dark',
    preview: ['#013743', '#0A3A52', '#9BDCEB', '#FFB703', '#FB8500'],
    cssVars: {
      '--color-bg': '#013743',
      '--color-bg-alt': '#0A3A52',
      '--color-bg-alt-soft': '#083449',
      '--color-border': '#0E4D64',
      '--color-text': '#E2E8F0',
      '--color-text-muted': '#9BDCEB',
      '--color-accent': '#FFB703',
      '--color-accent-strong': '#FB8500',
      '--color-on-accent': '#06202D',
      '--color-accent-soft': 'rgba(1, 0, 88, 0.16)',
      '--color-accent-strong-soft': 'rgba(251, 133, 0, 0.18)',
      '--color-overlay': 'rgba(1, 55, 67, 0.85)',
      '--color-tag-bg': 'rgba(255, 183, 3, 0.15)',
      '--color-tag-border': 'rgba(255, 183, 3, 0.35)',
      '--color-tag-text': '#FFB703',
      '--color-tag-alt-bg': 'rgba(251, 133, 0, 0.17)',
      '--color-tag-alt-border': 'rgba(251, 133, 0, 0.45)',
      '--color-tag-alt-text': '#FB8500',
      '--color-button-secondary-bg': '#0E4D64',
      '--color-button-secondary-bg-active': '#115D78',
      '--color-button-secondary-text': '#E8FAFF',
      '--color-button-primary-bg-active': '#E67600',
      '--color-info-border': '#9BDCEB',
      '--color-info-soft': 'rgba(155, 220, 235, 0.18)',
      '--color-win-text': '#06202D',
      '--color-cta-real-start': '#9BDCEB',
      '--color-cta-real-mid': '#FFD166',
      '--color-cta-real-end': '#FB8500',
      '--color-cta-real-text': '#06202D',
      '--color-cta-online-start': '#00B5E2',
      '--color-cta-online-mid': '#9BDCEB',
      '--color-cta-online-end': '#FFB703',
      '--color-cta-online-text': '#06202D',
      '--color-tunnel-bg': 'rgba(14, 77, 100, 0.78)',
      '--color-tunnel-border': '#115D78',
      '--color-tunnel-shadow': '0 8px 16px rgba(14, 77, 100, 0.18)',
      '--color-tunnel-text': '#C0F3FF',
    },
  },
  {
    id: 'ember-sunset',
    name: 'Żar zachodu',
    description: 'Wieczorne fiolety z ognistymi akcentami',
    colorScheme: 'dark',
    preview: ['#2B2D42', '#3F405A', '#8D99AE', '#EF233C', '#D90429'],
    cssVars: {
      '--color-bg': '#2B2D42',
      '--color-bg-alt': '#3F405A',
      '--color-bg-alt-soft': '#3B3C54',
      '--color-border': '#4E5068',
      '--color-text': '#EDF2F4',
      '--color-text-muted': '#8D99AE',
      '--color-accent': '#EF233C',
      '--color-accent-strong': '#D90429',
      '--color-on-accent': '#F9F9F9',
      '--color-accent-soft': 'rgba(239, 35, 60, 0.18)',
      '--color-accent-strong-soft': 'rgba(217, 4, 41, 0.2)',
      '--color-overlay': 'rgba(43, 45, 66, 0.85)',
      '--color-tag-bg': 'rgba(239, 35, 60, 0.15)',
      '--color-tag-border': 'rgba(239, 35, 60, 0.35)',
      '--color-tag-text': '#EF233C',
      '--color-tag-alt-bg': 'rgba(217, 4, 41, 0.17)',
      '--color-tag-alt-border': 'rgba(217, 4, 41, 0.4)',
      '--color-tag-alt-text': '#FFDDE0',
      '--color-button-secondary-bg': '#53567A',
      '--color-button-secondary-bg-active': '#616492',
      '--color-button-secondary-text': '#F5F7FB',
      '--color-button-primary-bg-active': '#C10321',
      '--color-info-border': '#8D99AE',
      '--color-info-soft': 'rgba(141, 153, 174, 0.2)',
      '--color-win-text': '#F9F9F9',
      '--color-cta-real-start': '#8D99AE',
      '--color-cta-real-mid': '#8B2FC9',
      '--color-cta-real-end': '#EF233C',
      '--color-cta-real-text': '#F9F9F9',
      '--color-cta-online-start': '#EF233C',
      '--color-cta-online-mid': '#8D99AE',
      '--color-cta-online-end': '#3F405A',
      '--color-cta-online-text': '#F9F9F9',
      '--color-tunnel-bg': 'rgba(217, 4, 41, 0.78)',
      '--color-tunnel-border': '#D90429',
      '--color-tunnel-shadow': '0 10px 20px rgba(217, 4, 41, 0.25)',
      '--color-tunnel-text': '#FFDDE0',
    },
  },
  {
    id: 'sage-meadow',
    name: 'Szałwiowa łąka',
    description: 'Soczysta zieleń z ciepłym złotem',
    colorScheme: 'dark',
    preview: ['#1B4332', '#2D6A4F', '#95D5B2', '#FFD166', '#FFC043'],
    cssVars: {
      '--color-bg': '#1B4332',
      '--color-bg-alt': '#24503E',
      '--color-bg-alt-soft': '#1E4A37',
      '--color-border': '#2F6F4F',
      '--color-text': '#E9F5EE',
      '--color-text-muted': '#95D5B2',
      '--color-accent': '#FFD166',
      '--color-accent-strong': '#FFC043',
      '--color-on-accent': '#2D3410',
      '--color-accent-soft': 'rgba(255, 209, 102, 0.18)',
      '--color-accent-strong-soft': 'rgba(255, 192, 67, 0.2)',
      '--color-overlay': 'rgba(27, 67, 50, 0.85)',
      '--color-tag-bg': 'rgba(255, 209, 102, 0.18)',
      '--color-tag-border': 'rgba(255, 209, 102, 0.4)',
      '--color-tag-text': '#FFD166',
      '--color-tag-alt-bg': 'rgba(255, 192, 67, 0.18)',
      '--color-tag-alt-border': 'rgba(255, 192, 67, 0.4)',
      '--color-tag-alt-text': '#1B4332',
      '--color-button-secondary-bg': '#2D6A4F',
      '--color-button-secondary-bg-active': '#357A59',
      '--color-button-secondary-text': '#E9F5EE',
      '--color-button-primary-bg-active': '#E9A72C',
      '--color-info-border': '#95D5B2',
      '--color-info-soft': 'rgba(149, 213, 178, 0.2)',
      '--color-win-text': '#1B3529',
      '--color-cta-real-start': '#95D5B2',
      '--color-cta-real-mid': '#FFD166',
      '--color-cta-real-end': '#FF8C42',
      '--color-cta-real-text': '#1B3529',
      '--color-cta-online-start': '#FFD166',
      '--color-cta-online-mid': '#95D5B2',
      '--color-cta-online-end': '#2D6A4F',
      '--color-cta-online-text': '#1B3529',
      '--color-tunnel-bg': 'rgba(255, 192, 67, 0.78)',
      '--color-tunnel-border': '#FFC043',
      '--color-tunnel-shadow': '0 10px 20px rgba(255, 192, 67, 0.25)',
      '--color-tunnel-text': '#1B3529',
    },
  },
  {
    id: 'dawn-mist',
    name: 'Poranna mgła',
    description: 'Jasne neutrals z ciepłym koralem',
    colorScheme: 'light',
    preview: ['#F4F1DE', '#FFF4E6', '#3D405B', '#E07A5F', '#81B29A'],
    cssVars: {
      '--color-bg': '#F4F1DE',
      '--color-bg-alt': '#FFF4E6',
      '--color-bg-alt-soft': '#EFE3CC',
      '--color-border': '#E6D3B1',
      '--color-text': '#3D405B',
      '--color-text-muted': '#6F748C',
      '--color-accent': '#E07A5F',
      '--color-accent-strong': '#CB6449',
      '--color-on-accent': '#FFFFFF',
      '--color-accent-soft': 'rgba(224, 122, 95, 0.18)',
      '--color-accent-strong-soft': 'rgba(203, 100, 73, 0.2)',
      '--color-overlay': 'rgba(244, 241, 222, 0.8)',
      '--color-tag-bg': 'rgba(224, 122, 95, 0.18)',
      '--color-tag-border': 'rgba(224, 122, 95, 0.38)',
      '--color-tag-text': '#CC553A',
      '--color-tag-alt-bg': 'rgba(129, 178, 154, 0.18)',
      '--color-tag-alt-border': 'rgba(129, 178, 154, 0.35)',
      '--color-tag-alt-text': '#234836',
      '--color-button-secondary-bg': '#81B29A',
      '--color-button-secondary-bg-active': '#6F9E86',
      '--color-button-secondary-text': '#1E2A23',
      '--color-button-primary-bg-active': '#B9543C',
      '--color-info-border': '#81B29A',
      '--color-info-soft': 'rgba(129, 178, 154, 0.24)',
      '--color-win-text': '#2D405B',
      '--color-cta-real-start': '#81B29A',
      '--color-cta-real-mid': '#F5C882',
      '--color-cta-real-end': '#E07A5F',
      '--color-cta-real-text': '#1E2A23',
      '--color-cta-online-start': '#E07A5F',
      '--color-cta-online-mid': '#F5C882',
      '--color-cta-online-end': '#81B29A',
      '--color-cta-online-text': '#1E2A23',
    },
  },
  {
    id: 'ultraviolet',
    name: 'Ultrafiolet',
    description: 'Fioletowe akcenty na ciemnym tle z różowymi detalami',
    colorScheme: 'dark',
    preview: ['#2A1B2E', '#4A1F4F', '#C084FC', '#7C3AED', '#5B21B6'],
    cssVars: {
      '--color-bg': '#2A1B2E',
      '--color-bg-alt': '#341E38',
      '--color-bg-alt-soft': '#2F1C34',
      '--color-border': '#4A1F4F',
      '--color-text': '#F7E9F5',
      '--color-text-muted': '#E3C8E8',
      '--color-accent': '#7C3AED',            /* przyciski = fiolet */
      '--color-accent-strong': '#5B21B6',
      '--color-on-accent': '#F5ECFF',
      '--color-accent-soft': 'rgba(124, 58, 237, 0.2)',
      '--color-accent-strong-soft': 'rgba(91, 33, 182, 0.24)',
      '--color-overlay': 'rgba(42, 27, 46, 0.85)',
      '--color-tag-bg': 'rgba(124, 58, 237, 0.16)',
      '--color-tag-border': 'rgba(124, 58, 237, 0.38)',
      '--color-tag-text': '#E3C8E8',
      '--color-tag-alt-bg': 'rgba(231, 81, 158, 0.18)',
      '--color-tag-alt-border': 'rgba(231, 81, 158, 0.4)',
      '--color-tag-alt-text': '#2A1B2E',
      '--color-button-secondary-bg': '#E7519E',         /* podrzędne na różowo */
      '--color-button-secondary-bg-active': '#B83280',
      '--color-button-secondary-text': '#250D24',
      '--color-button-primary-bg-active': '#4C1D95',
      '--color-info-border': '#E3C8E8',
      '--color-info-soft': 'rgba(227, 200, 232, 0.2)',
      '--color-win-text': '#F7E9F5',
      /* Dice palette */
      '--die-bg': '#F7A8D8',             /* jasny róż */
      '--die-dot': '#4A1F4F',
      '--die-border': '#B83280',
      '--die-kept-bg': '#E7519E',        /* głęboki róż na zaznaczone kości */
      '--die-kept-dot': '#2A1B2E',
      '--die-kept-border': '#F7C1E6',
      '--color-cta-real-start': '#FF3CAC',
      '--color-cta-real-mid': '#7C3AED',
      '--color-cta-real-end': '#2563EB',
      '--color-cta-real-text': '#F5ECFF',
      '--color-cta-online-start': '#C084FC',
      '--color-cta-online-mid': '#E7519E',
      '--color-cta-online-end': '#7C3AED',
      '--color-cta-online-text': '#F5ECFF',
    },
  },
  {
    id: 'rozy-roz',
    name: 'Róży róż',
    description: 'Jasny, jednolicie różowy motyw z delikatnymi kontrastami',
    colorScheme: 'light',
    preview: ['#FFF0F6', '#FFD9E8', '#FF8FB1', '#FF5C8D', '#E64A78'],
    cssVars: {
      '--color-bg': '#FFF0F6',
      '--color-bg-alt': '#FFD9E8',
      '--color-bg-alt-soft': '#FFE5EF',
      '--color-border': '#FFC2D8',
      '--color-text': '#6A1032',
      '--color-text-muted': '#A6365C',
      '--color-accent': '#FF5C8D',
      '--color-accent-strong': '#E64A78',
      '--color-on-accent': '#FFF7FB',
      '--color-accent-soft': 'rgba(255, 0, 241, 0.18)',
      '--color-accent-strong-soft': 'rgba(230, 74, 120, 0.2)',
      '--color-overlay': 'rgba(255, 240, 246, 0.9)',
      '--color-tag-bg': 'rgba(255, 92, 141, 0.15)',
      '--color-tag-border': 'rgba(255, 92, 141, 0.35)',
      '--color-tag-text': '#E64A78',
      '--color-tag-alt-bg': 'rgba(255, 143, 177, 0.18)',
      '--color-tag-alt-border': 'rgba(255, 143, 177, 0.4)',
      '--color-tag-alt-text': '#6A1032',
      '--color-button-secondary-bg': '#FF8FB1',
      '--color-button-secondary-bg-active': '#FF5C8D',
      '--color-button-secondary-text': '#6A1032',
      '--color-button-primary-bg-active': '#D93F70',
      '--color-info-border': '#FF8FB1',
      '--color-info-soft': 'rgba(255, 143, 177, 0.2)',
      '--color-win-text': '#6A1032',
      '--color-alert-bg': '#FFE5EF',
      '--color-alert-border': '#FF8FB1',
      '--color-alert-text': '#6A1032',
      '--color-warning-bg': '#FFEAF2',
      '--color-warning-border': '#FF8FB1',
      '--color-warning-text': '#6A1032',
      /* Dice palette */
      '--die-bg': '#FFD9E8',
      '--die-dot': '#6A1032',
      '--die-border': '#FF8FB1',
      '--die-kept-bg': '#FF5C8D',
      '--die-kept-dot': '#FFE5EF',
      '--die-kept-border': '#E64A78',
      '--color-cta-real-start': '#FF5C8D',
      '--color-cta-real-mid': '#FF8FB1',
      '--color-cta-real-end': '#FFD9E8',
      '--color-cta-real-text': '#6A1032',
      '--color-cta-online-start': '#E64A78',
      '--color-cta-online-mid': '#FF8FB1',
      '--color-cta-online-end': '#FFD9E8',
      '--color-cta-online-text': '#6A1032',
      '--color-tunnel-bg': 'rgba(230, 74, 120, 0.78)',
      '--color-tunnel-border': '#E64A78',
      '--color-tunnel-shadow': '0 10px 20px rgba(230, 74, 120, 0.25)',
      '--color-tunnel-text': '#6A1032',
    },
  },
  {
    id: 'choco-pumpkin',
    name: 'Czekoladowa dynia',
    description: 'Ciemne tła z aksamitnymi pomarańczami i bursztynem',
    colorScheme: 'dark',
    preview: ['#1F0A0A', '#2D0F0F', '#FFB703', '#FB8500', '#C1121F'],
    cssVars: {
      '--color-bg': '#1F0A0A',
      '--color-bg-alt': '#2D0F0F',
      '--color-bg-alt-soft': '#260C0C',
      '--color-border': '#3A1515',
      '--color-text': '#FDEFEF',
      '--color-text-muted': '#F4A261',
      '--color-accent': '#FFB703',
      '--color-accent-strong': '#FB8500',
      '--color-on-accent': '#220B0B',
      '--color-accent-soft': 'rgba(255, 183, 3, 0.18)',
      '--color-accent-strong-soft': 'rgba(251, 133, 0, 0.22)',
      '--color-overlay': 'rgba(31, 10, 10, 0.9)',
      '--color-tag-bg': 'rgba(255, 183, 3, 0.15)',
      '--color-tag-border': 'rgba(255, 183, 3, 0.35)',
      '--color-tag-text': '#FFB703',
      '--color-tag-alt-bg': 'rgba(193, 18, 31, 0.18)',
      '--color-tag-alt-border': 'rgba(193, 18, 31, 0.4)',
      '--color-tag-alt-text': '#FDEFEF',
      '--color-button-secondary-bg': '#441414',
      '--color-button-secondary-bg-active': '#5A1B1B',
      '--color-button-secondary-text': '#FDEFEF',
      '--color-button-primary-bg-active': '#D95F02',
      '--color-info-border': '#F4A261',
      '--color-info-soft': 'rgba(244, 162, 97, 0.2)',
      '--color-win-text': '#1F0A0A',
      '--color-cta-real-start': '#FFB703',
      '--color-cta-real-mid': '#FB8500',
      '--color-cta-real-end': '#C1121F',
      '--color-cta-real-text': '#220B0B',
      '--color-cta-online-start': '#C1121F',
      '--color-cta-online-mid': '#FB8500',
      '--color-cta-online-end': '#FFB703',
      '--color-cta-online-text': '#220B0B',
      '--color-tunnel-bg': 'rgba(251, 133, 0, 0.78)',
      '--color-tunnel-border': '#FB8500',
      '--color-tunnel-shadow': '0 10px 20px rgba(251, 133, 0, 0.25)',
      '--color-tunnel-text': '#FFECC2',
    },
  },
  {
    id: 'flame',
    name: 'Płomień',
    description: 'Jasne, żywe żółcie z pomarańczowo-czerwonymi akcentami',
    colorScheme: 'light',
    preview: ['#FFF7D1', '#FFE188', '#FFB703', '#FB8500', '#E63946'],
    cssVars: {
      '--color-bg': '#FFF7D1',
      '--color-bg-alt': '#FFC766',
      '--color-bg-alt-soft': '#FFD68A',
      '--color-border': '#F56B2A',
      '--color-text': '#2F1A00',
      '--color-text-muted': '#C44D2D',
      '--color-accent': '#E63946',
      '--color-accent-strong': '#FB8500',
      '--color-on-accent': '#FFF7D1',
      '--color-accent-soft': 'rgba(230, 57, 70, 0.16)',
      '--color-accent-strong-soft': 'rgba(251, 133, 0, 0.18)',
      '--color-overlay': 'rgba(255, 247, 209, 0.92)',
      '--color-tag-bg': 'rgba(255, 183, 3, 0.18)',
      '--color-tag-border': 'rgba(255, 183, 3, 0.38)',
      '--color-tag-text': '#C44D2D',
      '--color-tag-alt-bg': 'rgba(230, 57, 70, 0.16)',
      '--color-tag-alt-border': 'rgba(230, 57, 70, 0.36)',
      '--color-tag-alt-text': '#9C1E2A',
      '--color-button-secondary-bg': '#FFE188',
      '--color-button-secondary-bg-active': '#FFB703',
      '--color-button-secondary-text': '#2F1A00',
      '--color-button-primary-bg-active': '#FB8500',
      '--color-info-border': '#FFB703',
      '--color-info-soft': 'rgba(255, 183, 3, 0.22)',
      '--color-win-text': '#2F1A00',
      '--color-cta-real-start': '#FFE188',
      '--color-cta-real-mid': '#FB8500',
      '--color-cta-real-end': '#E63946',
      '--color-cta-real-text': '#2F1A00',
      '--color-cta-online-start': '#E63946',
      '--color-cta-online-mid': '#FB8500',
      '--color-cta-online-end': '#FFB703',
      '--color-cta-online-text': '#2F1A00',
      '--color-tunnel-bg': 'rgba(255, 183, 3, 0.78)',
      '--color-tunnel-border': '#FB8500',
      '--color-tunnel-shadow': '0 10px 20px rgba(230, 57, 70, 0.25)',
      '--color-tunnel-text': '#5A1A00',
    },
  },
  {
    id: 'pizza',
    name: 'Pizza, pasta, buona sera',
    description: 'Motyw flagi Włoch: biel, zieleń i czerwień jak świeża margherita',
    colorScheme: 'light',
    preview: ['#F4F5F0', '#009246', '#CE2B37', '#F9F7F2', '#E5E5DD'],
    cssVars: {
      '--color-bg': '#F4F5F0',
      '--color-bg-alt': '#E5E5DD',
      '--color-bg-alt-soft': '#F9F7F2',
      '--color-border': '#C9CCC2',
      '--color-text': '#1A1F12',
      '--color-text-muted': '#5A5F51',
      '--color-accent': '#CE2B37',
      '--color-accent-strong': '#9E1E2A',
      '--color-on-accent': '#FFF7F7',
      '--color-accent-soft': 'rgba(206, 43, 55, 0.16)',
      '--color-accent-strong-soft': 'rgba(158, 30, 42, 0.18)',
      '--color-overlay': 'rgba(244, 245, 240, 0.9)',
      '--color-tag-bg': 'rgba(0, 146, 70, 0.15)',
      '--color-tag-border': 'rgba(0, 146, 70, 0.35)',
      '--color-tag-text': '#0A7B3F',
      '--color-tag-alt-bg': 'rgba(206, 43, 55, 0.15)',
      '--color-tag-alt-border': 'rgba(206, 43, 55, 0.38)',
      '--color-tag-alt-text': '#9E1E2A',
      '--color-button-secondary-bg': '#009246',
      '--color-button-secondary-bg-active': '#0A7B3F',
      '--color-button-secondary-text': '#F4F5F0',
      '--color-button-primary-bg-active': '#CE2B37',
      '--color-info-border': '#0A7B3F',
      '--color-info-soft': 'rgba(0, 146, 70, 0.16)',
      '--color-win-text': '#0A7B3F',
      '--color-cta-online-start': '#009246',
      '--color-cta-online-mid': '#FFFFFF',
      '--color-cta-online-end': '#CE2B37',
      '--color-cta-online-text': '#1A1F12',
      '--color-cta-real-start': '#CE2B37',
      '--color-cta-real-mid': '#FFFFFF',
      '--color-cta-real-end': '#009246',
      '--color-cta-real-text': '#1A1F12',
      '--color-tunnel-bg': 'rgba(0, 146, 70, 0.78)',
      '--color-tunnel-border': '#0A7B3F',
      '--color-tunnel-shadow': '0 8px 16px rgba(0, 146, 70, 0.18)',
      '--color-tunnel-text': '#0A2F1A',
    },
  },
  {
    id: 'matrix',
    name: 'Matrix',
    description: 'Czerń z neonową zielenią',
    colorScheme: 'dark',
    preview: ['#050505', '#0B0F0C', '#36BA01', '#009A22', '#00FF2B'],
    cssVars: {
      '--color-bg': '#050505',
      '--color-bg-alt': '#0B0F0C',
      '--color-bg-alt-soft': '#0A1209',
      '--color-border': '#0F1A11',
      '--color-text': '#D5FFD5',
      '--color-text-muted': '#7EE46F',
      '--color-accent': '#36BA01',
      '--color-accent-strong': '#00FF2B',
      '--color-on-accent': '#041204',
      '--color-accent-soft': 'rgba(54, 186, 1, 0.18)',
      '--color-accent-strong-soft': 'rgba(0, 255, 43, 0.2)',
      '--color-overlay': 'rgba(5, 5, 5, 0.85)',
      '--color-tag-bg': 'rgba(54, 186, 1, 0.16)',
      '--color-tag-border': 'rgba(54, 186, 1, 0.38)',
      '--color-tag-text': '#00FF2B',
      '--color-tag-alt-bg': 'rgba(0, 154, 34, 0.18)',
      '--color-tag-alt-border': 'rgba(0, 154, 34, 0.4)',
      '--color-tag-alt-text': '#36BA01',
      '--color-button-secondary-bg': '#0F1A11',
      '--color-button-secondary-bg-active': '#142418',
      '--color-button-secondary-text': '#D5FFD5',
      '--color-button-primary-bg-active': '#00D926',
      '--color-info-border': '#36BA01',
      '--color-info-soft': 'rgba(54, 186, 1, 0.22)',
      '--color-win-text': '#041204',
      '--color-cta-real-start': '#D5FFD5',
      '--color-cta-real-mid': '#00FF2B',
      '--color-cta-real-end': '#36BA01',
      '--color-cta-real-text': '#041204',
      '--color-cta-online-start': '#36BA01',
      '--color-cta-online-mid': '#00FF2B',
      '--color-cta-online-end': '#D5FFD5',
      '--color-cta-online-text': '#041204',
      '--color-tunnel-bg': 'rgba(0, 154, 34, 0.78)',
      '--color-tunnel-border': '#009A22',
      '--color-tunnel-shadow': '0 10px 20px rgba(0, 154, 34, 0.25)',
      '--color-tunnel-text': '#B9FFB9',
    },
  },
  {
    id: 'rainbow',
    name: 'Rainbow',
    description: 'Nasycona tęcza: czerwienie, żółcie, błękity i fiolety na jasnym tle',
    colorScheme: 'light',
    preview: ['#FFF8E7', '#FF3B30', '#FFD60A', '#2563EB', '#7C3AED'],
    cssVars: {
      '--color-bg': '#FFF8E7',
      '--color-bg-alt': '#FFF1C7',
      '--color-bg-alt-soft': '#FFE7A6',
      '--color-border': '#F0C96B',
      '--color-text': '#24160A',
      '--color-text-muted': '#7A5B20',
      '--color-accent': '#C0180C',
      '--color-accent-strong': '#7C3AED',
      '--color-on-accent': '#FFF9F4',
      '--color-accent-soft': 'rgba(192, 24, 12, 0.16)',
      '--color-screen-header-title': '#24160A',
      '--color-accent-strong-soft': 'rgba(124, 58, 237, 0.18)',
      '--color-overlay': 'rgba(255, 248, 231, 0.9)',
      '--color-tag-bg': 'rgba(255, 214, 10, 0.24)',
      '--color-tag-border': 'rgba(255, 214, 10, 0.52)',
      '--color-tag-text': '#8A5A00',
      '--color-tag-alt-bg': 'rgba(37, 99, 235, 0.16)',
      '--color-tag-alt-border': 'rgba(37, 99, 235, 0.38)',
      '--color-tag-alt-text': '#0D3695',
      '--color-button-secondary-bg': '#2F7DF6',
      '--color-button-secondary-bg-active': '#1F63D8',
      '--color-button-secondary-text': '#FFF8F2',
      '--color-button-primary-bg-active': '#FF8C00',
      '--color-info-border': '#2563EB',
      '--color-info-soft': 'rgba(37, 99, 235, 0.16)',
      '--color-win-text': '#24160A',
      '--color-cta-real-start': '#FFD60A',
      '--color-cta-real-mid': '#FF8C00',
      '--color-cta-real-end': '#2563EB',
      '--color-cta-real-text': '#24160A',
      '--color-tunnel-bg': 'rgba(255, 214, 10, 0.78)',
      '--color-tunnel-border': '#FF8C00',
      '--color-tunnel-shadow': '0 10px 20px rgba(124, 58, 237, 0.18)',
      '--color-tunnel-text': '#5B2C00',
      '--color-cta-online-start': '#FF3B30',
      '--color-cta-online-mid': '#FFD60A',
      '--color-cta-online-end': '#7C3AED',
      '--color-cta-online-text': '#24160A',
    },
  },
  {
    id: 'psychedellic',
    name: 'Psychedellic',
    description: 'Psychodeliczne lata 60.: żarówiasta czerwień, magenta, cyjan i kwasowa żółć',
    colorScheme: 'dark',
    preview: ['#8A001F', '#FF5ACD', '#D6FF00', '#35E0FF', '#FF8A00'],
    cssVars: {
      '--color-bg': '#180824',
      '--color-bg-alt': '#27103A',
      '--color-bg-alt-soft': '#210F31',
      '--color-border': '#5D2E7A',
      '--color-text': '#FFF5D9',
      '--color-text-muted': '#D7C1FF',
      '--color-accent': '#FF80DA',
      '--color-accent-strong': '#35E0FF',
      '--color-on-accent': '#16091F',
      '--color-accent-soft': 'rgba(255, 128, 218, 0.22)',
      '--color-screen-header-title': '#D93396',
      '--color-accent-strong-soft': 'rgba(53, 224, 255, 0.2)',
      '--color-overlay': 'rgba(18, 8, 36, 0.9)',
      '--color-tag-bg': 'rgba(214, 255, 0, 0.18)',
      '--color-tag-border': 'rgba(214, 255, 0, 0.45)',
      '--color-tag-text': '#F6FFB2',
      '--color-tag-alt-bg': 'rgba(255, 138, 0, 0.18)',
      '--color-tag-alt-border': 'rgba(255, 138, 0, 0.42)',
      '--color-tag-alt-text': '#FFE6C5',
      '--color-button-secondary-bg': '#2C1450',
      '--color-button-secondary-bg-active': '#3A1B68',
      '--color-button-secondary-text': '#FFF6FF',
      '--color-button-primary-bg-active': '#FF8A00',
      '--color-info-border': '#35E0FF',
      '--color-info-soft': 'rgba(53, 224, 255, 0.24)',
      '--color-win-text': '#16091F',
      '--color-cta-real-start': '#D6FF00',
      '--color-cta-real-mid': '#FF5ACD',
      '--color-cta-real-end': '#35E0FF',
      '--color-cta-real-text': '#16091F',
      '--color-tunnel-bg': 'rgba(124, 92, 255, 0.78)',
      '--color-tunnel-border': '#7B5CFF',
      '--color-tunnel-shadow': '0 10px 24px rgba(53, 224, 255, 0.2)',
      '--color-tunnel-text': '#FFF5D9',
      '--color-cta-online-start': '#FF8A00',
      '--color-cta-online-mid': '#D6FF00',
      '--color-cta-online-end': '#35E0FF',
      '--color-cta-online-text': '#16091F',
    },
  },
  {
    id: 'ja-nie-wymyslam',
    name: 'Ja nie wymyślam',
    description: 'Energetyczne czerwienie, fiolety i błękit',
    colorScheme: 'dark',
    preview: ['#0F0A14', '#1A1024', '#D90404', '#9704BF', '#F29F05'],
    cssVars: {
      '--color-bg': '#0F0A14',
      '--color-bg-alt': '#1A1024',
      '--color-bg-alt-soft': '#160D1F',
      '--color-border': '#261436',
      '--color-text': '#F7ECFF',
      '--color-text-muted': '#DAB7F6',
      '--color-accent': '#D90404',
      '--color-accent-strong': '#9704BF',
      '--color-on-accent': '#1A0C20',
      '--color-accent-soft': 'rgba(217, 4, 4, 0.2)',
      '--color-accent-strong-soft': 'rgba(151, 4, 191, 0.22)',
      '--color-overlay': 'rgba(15, 10, 20, 0.88)',
      '--color-tag-bg': 'rgba(242, 159, 5, 0.18)',
      '--color-tag-border': 'rgba(242, 159, 5, 0.4)',
      '--color-tag-text': '#1F1200',
      '--color-tag-alt-bg': 'rgba(28, 86, 222, 0.2)',
      '--color-tag-alt-border': 'rgba(28, 86, 222, 0.42)',
      '--color-tag-alt-text': '#061231',
      '--color-button-secondary-bg': '#261436',
      '--color-button-secondary-bg-active': '#301A44',
      '--color-button-secondary-text': '#F7ECFF',
      '--color-button-primary-bg-active': '#F27405',
      '--color-info-border': '#1C56DE',
      '--color-info-soft': 'rgba(28, 86, 222, 0.24)',
      '--color-win-text': '#0F0A14',
      '--color-cta-real-start': '#9704BF',
      '--color-cta-real-mid': '#D90404',
      '--color-cta-real-end': '#F29F05',
      '--color-cta-real-text': '#190C24',
      '--color-tunnel-bg': 'rgba(217, 4, 4, 0.78)',
      '--color-tunnel-border': '#D90404',
      '--color-tunnel-shadow': '0 10px 20px rgba(217, 4, 4, 0.22)',
      '--color-tunnel-text': '#FDE6E6',
      '--color-cta-online-start': '#D90404',
      '--color-cta-online-mid': '#F29F05',
      '--color-cta-online-end': '#1C56DE',
      '--color-cta-online-text': '#F7ECFF',
    },
  },
];

const themesInternal: ThemeDefinition[] = baseThemesInternal.map((theme) => ({
  ...theme,
  cssVars: {
    ...METRIC_COLOR_VARS_BY_SCHEME[theme.colorScheme],
    ...theme.cssVars,
  },
}));

export const DEFAULT_THEME_ID: ThemeId = 'deep-sea';

const themeMap = new Map<ThemeId, ThemeDefinition>(themesInternal.map((theme) => [theme.id, theme]));
const ALL_THEME_VARS = Array.from(
  themesInternal.reduce((acc, theme) => {
    Object.keys(theme.cssVars).forEach((key) => acc.add(key));
    return acc;
  }, new Set<string>()),
);

export function isValidThemeId(id: unknown): id is ThemeId {
  return typeof id === 'string' && themeMap.has(id as ThemeId);
}

export function getTheme(id: string | null | undefined): ThemeDefinition {
  if (typeof id === 'string' && themeMap.has(id as ThemeId)) {
    return themeMap.get(id as ThemeId)!;
  }
  return themeMap.get(DEFAULT_THEME_ID)!;
}

const THEME_STORAGE_KEY = 'dice1k_theme';

export function applyTheme(themeId: string): void {
  if (typeof document === 'undefined') return;
  const theme = getTheme(themeId);
  const root = document.documentElement;
  root.dataset.theme = theme.id;
  root.style.colorScheme = theme.colorScheme;
  ALL_THEME_VARS.forEach((name) => root.style.removeProperty(name));
  Object.entries(theme.cssVars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function readStoredThemeId(): string | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored && isValidThemeId(stored) ? stored : null;
}

export function storeThemeId(themeId: string): void {
  if (typeof window === 'undefined') return;
  const next = getTheme(themeId).id;
  window.localStorage.setItem(THEME_STORAGE_KEY, next);
}

export function getThemeList(): ThemeDefinition[] {
  return themesInternal;
}
